import React, { type ReactNode, useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { type CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { MenuButtonItem } from '@atlassian/navigation-system/side-nav/menu-button-item';
import { MenuLinkItem as MenuLinkItemPrimitive } from '@atlassian/navigation-system/side-nav/menu-link-item';

import { ANALYTICS_CHANNEL } from '../../constants';
import type { GlobalShortcutActionsItem, GlobalShortcutLinkItem } from '../../types';
import { MenuLoadingItem } from '../menu-loading-item';
import { ShortcutActionMenu } from '../shortcut-action';

import { messages } from './messages';

export type MenuLinkItemProps = GlobalShortcutLinkItem & {
	children?: ReactNode;
	url?: string;
	disabled?: boolean;
	onDisable?: (reason?: string) => void;
	ariaLabel?: string;
	loading?: boolean;
	actions?: GlobalShortcutActionsItem[];
};

export const fireMenuLinkItemClickAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'globalNavigation',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
};

const styles = cssMap({
	iconsBlock: {
		paddingInline: token('space.075'),
	},
});

// renders null if url is undefined, renders disabled if url is null but disabled is true, renders link item if url has a value
export const MenuLinkItem = ({
	analyticsName,
	url,
	label,
	icon,
	disabled,
	onDisable,
	ariaLabel,
	loading,
	actions,
}: MenuLinkItemProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
	}, [createAnalyticsEvent, analyticsName]);

	// If loading is true, we want to render a representation of the button
	if (loading === true) {
		return <MenuLoadingItem icon={icon} label={label} />;
	}

	// Don't render anything if url is undefined or empty, and disabled is not explicitly set to true
	if ((!url || url === '') && !disabled) {
		return null;
	}

	// Render disabled items if no url and/or disabled is true
	if (!url || disabled) {
		if (!url || url === '') {
			onDisable?.('Invalid URL');
		} else {
			onDisable?.();
		}

		return (
			<MenuButtonItem elemBefore={icon} isDisabled>
				{label}
				<span aria-label={formatMessage(messages.disabledItemAriaLabel)} />
			</MenuButtonItem>
		);
	}

	// Only render link item if url is defined
	return (
		<MenuLinkItemPrimitive
			actionsOnHover={actions && actions.length && <ShortcutActionMenu actions={actions} />}
			elemBefore={icon}
			elemAfter={
				<Box xcss={cx(styles.iconsBlock)}>
					<LinkExternalIcon label={formatMessage(messages.linkExternalIconLabel)} size="small" />
				</Box>
			}
			href={url}
			target="_blank"
			onClick={onClick}
		>
			<span aria-label={ariaLabel}>{label}</span>
		</MenuLinkItemPrimitive>
	);
};
